import React from 'react';
import { MediaLibraryItem } from '../../../api/media';
import Modal from '../../modals/Modal';
import { Button } from '../../ui';
import {
  copyGenerationRecipe,
  readGenerationRecipe,
  sanitizeRecipeForDisplay,
} from './MediaMetadataInspector';

export { sanitizeRecipeForDisplay } from './MediaMetadataInspector';

interface GenerationRecipeModalProps {
  item: MediaLibraryItem;
  isOpen: boolean;
  onClose: () => void;
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const displayValue = (value: unknown): string => {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  return JSON.stringify(sanitizeRecipeForDisplay(value));
};

interface RecipeRow {
  label: string;
  value: unknown;
}

const RecipeRows: React.FC<{ rows: RecipeRow[] }> = ({ rows }) => {
  const visible = rows.filter((row) => row.value !== undefined && row.value !== null && row.value !== '');
  if (visible.length === 0) return <p className="text-xs text-zinc-500">Not recorded.</p>;
  return (
    <dl className="grid grid-cols-[minmax(8rem,0.35fr)_minmax(0,1fr)] gap-x-4 gap-y-2 text-xs">
      {visible.map((row) => (
        <React.Fragment key={row.label}>
          <dt className="text-zinc-500 uppercase tracking-wider">{row.label}</dt>
          <dd className="text-zinc-200 whitespace-pre-wrap break-words min-w-0">{displayValue(row.value)}</dd>
        </React.Fragment>
      ))}
    </dl>
  );
};

const RecipeSection: React.FC<React.PropsWithChildren<{ title: string }>> = ({ title, children }) => (
  <section className="border border-white/10 bg-black/20 p-3 space-y-3">
    <h3 className="text-xs font-semibold uppercase tracking-[0.18em] text-zinc-300">{title}</h3>
    {children}
  </section>
);

const ObjectList: React.FC<{ value: unknown; empty: string }> = ({ value, empty }) => {
  if (!Array.isArray(value) || value.length === 0) return <p className="text-xs text-zinc-500">{empty}</p>;
  return (
    <div className="space-y-2">
      {value.filter(isRecord).map((entry, index) => (
        <div key={index} className="border-l-2 border-indigo-400/50 pl-3">
          <RecipeRows rows={Object.entries(sanitizeRecipeForDisplay(entry) as Record<string, unknown>).map(([label, rowValue]) => ({ label, value: rowValue }))} />
        </div>
      ))}
    </div>
  );
};

const GenerationRecipeModal: React.FC<GenerationRecipeModalProps> = ({ item, isOpen, onClose }) => {
  const recipe = readGenerationRecipe(item) ?? {};
  const baseModel = isRecord(recipe.base_model) ? recipe.base_model : {};
  const prompt = isRecord(recipe.prompt) ? recipe.prompt : {};
  const params = isRecord(recipe.params) ? recipe.params : {};
  const ipAdapter = isRecord(recipe.ip_adapter) ? recipe.ip_adapter : undefined;
  const metadata = isRecord(item.metadata) ? item.metadata : {};
  const raw = JSON.stringify(sanitizeRecipeForDisplay(recipe), null, 2);

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Generation recipe" size="lg">
      <div className="space-y-3" data-testid="generation-recipe-modal">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="text-xs text-zinc-400">Settings recorded for recreating a similar result.</p>
          <Button type="button" variant="secondary" size="sm" onClick={() => copyGenerationRecipe(item)}>
            Copy recipe
          </Button>
        </div>

        <RecipeSection title="Model and runtime">
          <RecipeRows rows={[
            { label: 'Schema', value: recipe.schema_version },
            { label: 'Provider', value: recipe.provider },
            { label: 'Runtime', value: recipe.runtime_id },
            { label: 'Model', value: baseModel.resolved_id ?? baseModel.id },
            { label: 'Family', value: baseModel.family },
          ]} />
        </RecipeSection>

        <RecipeSection title="Prompts">
          <RecipeRows rows={[
            { label: 'Requested', value: prompt.requested ?? item.prompt },
            { label: 'Effective', value: prompt.effective },
            { label: 'Negative requested', value: prompt.negative_requested },
            { label: 'Negative effective', value: prompt.negative_effective },
          ]} />
        </RecipeSection>

        <RecipeSection title="Sampling">
          <RecipeRows rows={[
            { label: 'Mode', value: params.mode },
            { label: 'Size', value: params.width && params.height ? `${params.width}×${params.height}` : undefined },
            { label: 'Seed', value: params.seed },
            { label: 'Steps', value: params.steps },
            { label: 'CFG', value: params.cfg_scale },
            { label: 'Scheduler', value: params.scheduler },
            { label: 'Denoise', value: params.denoise_strength },
            { label: 'Clip skip', value: params.clip_skip },
            { label: 'VAE', value: params.vae_id },
            { label: 'GPU', value: params.gpu },
          ]} />
        </RecipeSection>

        <div className="grid gap-3 md:grid-cols-2">
          <RecipeSection title="LoRA adapters">
            <ObjectList value={recipe.adapters} empty="No LoRA adapter recorded." />
          </RecipeSection>
          <RecipeSection title="ControlNet">
            <ObjectList value={recipe.controls} empty="No ControlNet recorded." />
          </RecipeSection>
        </div>

        <RecipeSection title="IP-Adapter and post-processing">
          <RecipeRows rows={[
            { label: 'IP-Adapter', value: ipAdapter },
            { label: 'Post', value: recipe.post },
            { label: 'Quality guard', value: recipe.quality_guard },
          ]} />
        </RecipeSection>

        <RecipeSection title="Persistence">
          <RecipeRows rows={[
            { label: 'Status', value: metadata.persistence_status ?? item.status },
            { label: 'Durable original', value: item.has_file },
            { label: 'Failure stage', value: metadata.persistence_error_stage },
            { label: 'Failure', value: metadata.persistence_error },
          ]} />
        </RecipeSection>

        <details className="border border-white/10 bg-black/30 p-3">
          <summary className="cursor-pointer text-xs uppercase tracking-wider text-zinc-400">Raw JSON</summary>
          <pre className="mt-3 max-h-80 overflow-auto whitespace-pre-wrap break-words text-[11px] text-zinc-300">{raw}</pre>
        </details>
      </div>
    </Modal>
  );
};

export default GenerationRecipeModal;
